import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface CaptureElementPdfOptions {
  fileName: string;
  orientation?: 'landscape' | 'portrait';
  /** ข้อความท้ายหน้า — jsPDF ฟอนต์มาตรฐานไม่รองรับภาษาไทย ใช้ ASCII เท่านั้น */
  footerText?: string;
  /** ปรับ DOM ที่ clone แล้วก่อนแคป (เช่น แทนกราฟที่ html2canvas วาดไม่ได้) */
  onClone?: (clonedRoot: HTMLElement, clonedDocument: Document) => void;
}

const PAGE_MARGIN_MM = 8;
const FOOTER_MM = 6;

/** html2canvas 1.4 ไม่รู้จักสี oklch / oklab / color() ของ Tailwind v4 — ต้องแปลงเป็น rgb ก่อน */
const MODERN_COLOR_PATTERN = /(?:oklch|oklab|lab|lch|color)\([^()]*\)/g;
const HAS_MODERN_COLOR = /(?:oklch|oklab|lab|lch|color)\(/;
const COLOR_PROPS = [
  'color',
  'background-color',
  'background-image',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'box-shadow',
  'text-shadow',
  'text-decoration-color',
  '-webkit-text-stroke-color',
  'outline-color',
  'fill',
  'stroke',
];

/**
 * แคป element เป็น PDF (A4) — ตัดหน้าตามลูกตรงของ element เพื่อไม่ให้การ์ด/กราฟขาดกลาง
 * บังคับธีมสว่าง และปิด animation ใน clone
 */
export async function exportElementToPdf(
  element: HTMLElement,
  options: CaptureElementPdfOptions,
): Promise<void> {
  const rootRect = element.getBoundingClientRect();
  const blocks = (Array.from(element.children) as HTMLElement[])
    .filter((child) => child.offsetHeight > 0)
    .map((child) => {
      const rect = child.getBoundingClientRect();
      return { top: rect.top - rootRect.top, bottom: rect.bottom - rootRect.top };
    });
  const totalHeight = element.scrollHeight;
  const cssWidth = element.offsetWidth;

  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    onclone: (clonedDocument, clonedRoot) => {
      prepareClone(clonedDocument, clonedRoot);
      options.onClone?.(clonedRoot, clonedDocument);
    },
  });

  const orientation = options.orientation ?? 'landscape';
  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN_MM * 2;
  const mmPerPx = contentWidth / cssWidth;
  const pageCssHeight = (pageHeight - PAGE_MARGIN_MM * 2 - FOOTER_MM) / mmPerPx;
  const pxScale = canvas.width / cssWidth;

  const pages = paginate(blocks, totalHeight, pageCssHeight);
  pages.forEach(([start, end], index) => {
    if (index > 0) pdf.addPage();

    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = Math.max(1, Math.round((end - start) * pxScale));
    const ctx = slice.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(
      canvas,
      0, Math.round(start * pxScale), canvas.width, slice.height,
      0, 0, slice.width, slice.height,
    );

    pdf.addImage(
      slice.toDataURL('image/jpeg', 0.92),
      'JPEG',
      PAGE_MARGIN_MM,
      PAGE_MARGIN_MM,
      contentWidth,
      (end - start) * mmPerPx,
    );

    pdf.setFontSize(7);
    pdf.setTextColor(148, 163, 184);
    const footer = [options.footerText, `Page ${index + 1} / ${pages.length}`].filter(Boolean).join('   |   ');
    pdf.text(footer, pageWidth / 2, pageHeight - PAGE_MARGIN_MM / 2, { align: 'center' });
  });

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    window.open(pdf.output('bloburl'));
  } else {
    pdf.save(options.fileName);
  }
}

function paginate(
  blocks: { top: number; bottom: number }[],
  totalHeight: number,
  pageHeight: number,
): [number, number][] {
  const pages: [number, number][] = [];
  let start = 0;

  for (const block of blocks) {
    if (block.bottom - start <= pageHeight) continue;
    if (block.top > start) {
      pages.push([start, block.top]);
      start = block.top;
    }
    while (block.bottom - start > pageHeight) {
      pages.push([start, start + pageHeight]);
      start += pageHeight;
    }
  }

  if (totalHeight > start + 1) pages.push([start, totalHeight]);
  return pages.length > 0 ? pages : [[0, Math.max(1, totalHeight)]];
}

function prepareClone(clonedDocument: Document, clonedRoot: HTMLElement): void {
  clonedDocument.documentElement.classList.remove('dark');

  const style = clonedDocument.createElement('style');
  style.textContent = '*, *::before, *::after { animation: none !important; transition: none !important; }';
  clonedDocument.head.appendChild(style);

  const view = clonedDocument.defaultView;
  if (!view) return;

  const elements = [clonedRoot, ...Array.from(clonedRoot.querySelectorAll<HTMLElement | SVGElement>('*'))];
  for (const el of elements) {
    const computed = view.getComputedStyle(el);
    if (el instanceof view.SVGElement) {
      for (const prop of ['color', 'fill', 'stroke']) {
        const value = computed.getPropertyValue(prop);
        if (value) el.style.setProperty(prop, value.replace(MODERN_COLOR_PATTERN, resolveCssColor));
      }
    }
    for (const prop of COLOR_PROPS) {
      const value = computed.getPropertyValue(prop);
      if (!value || !HAS_MODERN_COLOR.test(value)) continue;
      el.style.setProperty(prop, value.replace(MODERN_COLOR_PATTERN, resolveCssColor), 'important');
    }
  }
}

const colorCache = new Map<string, string>();
let colorCtx: CanvasRenderingContext2D | null = null;

function resolveCssColor(color: string): string {
  const cached = colorCache.get(color);
  if (cached) return cached;

  if (!colorCtx) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    colorCtx = canvas.getContext('2d', { willReadFrequently: true });
  }
  if (!colorCtx) return 'rgb(0, 0, 0)';

  colorCtx.clearRect(0, 0, 1, 1);
  colorCtx.fillStyle = '#000000';
  colorCtx.fillStyle = color;
  colorCtx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = colorCtx.getImageData(0, 0, 1, 1).data;
  const resolved = a >= 255 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
  colorCache.set(color, resolved);
  return resolved;
}
